import { useMemo } from 'react';
import { json } from '@codemirror/lang-json';
import { okaidiaInit } from '@uiw/codemirror-theme-okaidia';
import CodeMirror from '@uiw/react-codemirror';
import { toast } from 'react-toastify';

import { Button, Modal, ModalActions } from '~/components/ui';
import { themeColor } from '~/lib/theme-colors';

export interface SchemaJsonPreviewModalProps {
  json: string;
  onClose: () => void;
}

/** The business-fields view of the schema: each field's name and the token that puts it in the prompt (DESIGN.md §8.1 code surface). */
export default function SchemaJsonPreviewModal({
  json: jsonText,
  onClose,
}: SchemaJsonPreviewModalProps) {
  const extensions = useMemo(() => [json()], []);
  // A library color cannot read classes, so the caret comes from the token (DESIGN.md §3.6).
  const theme = useMemo(
    () => okaidiaInit({ settings: { caret: themeColor('gray-300'), fontFamily: 'monospace' } }),
    [],
  );

  const copy = () => {
    if (!navigator.clipboard) {
      toast.error('Failed to copy the JSON preview to the clipboard.');
      return;
    }

    navigator.clipboard
      .writeText(jsonText)
      .then(() => toast.success('JSON copied'))
      .catch(() => toast.error('Failed to copy the JSON preview to the clipboard.'));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Business fields preview"
      description="Find a field by its business_field name, then copy its use_in_prompt value into the prompt."
      testId="ai-digital-assistants-schema-preview-modal"
      footer={
        <ModalActions
          onDismiss={onClose}
          dismissTestId="ai-digital-assistants-schema-preview-close"
        />
      }
    >
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={copy}
            testId="ai-digital-assistants-schema-preview-copy"
          >
            Copy JSON
          </Button>
        </div>
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
          <CodeMirror
            aria-label="Business fields preview"
            value={jsonText}
            height="420px"
            theme={theme}
            extensions={extensions}
            editable={false}
            basicSetup={{ foldGutter: true, highlightActiveLine: false, lineNumbers: true }}
          />
        </div>
      </div>
    </Modal>
  );
}
